import styled from 'styled-components';

import Alert from 'components/shared/Alert';
import { dateAtTimeNoRelative, simpleDate } from 'lib/date';
import LocationEpisodeDischarge from 'models/LocationEpisodeDischarge';
import PccDischargeReport, { PccDischargeReviewReason } from 'models/PccDischargeReport';
import colors from 'styles/theme/colors';

type Props = {
  report: PccDischargeReport;
  locationEpisodeDischarge: LocationEpisodeDischarge;
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const REVIEW_REASON_NOTES: Record<PccDischargeReviewReason, string> = {
  [PccDischargeReviewReason.NoMatchingDisposition]:
    "PointClickCare's discharge values didn't match an Olio disposition.",
  [PccDischargeReviewReason.ReferralDidNotFit]:
    "The patient had an accepted referral that PointClickCare's discharge didn't go to.",
};

export function PccDischargeBanner({ report, locationEpisodeDischarge }: Props) {
  const filledIn = report.filledIn(locationEpisodeDischarge.id);
  const edited = !!report.locationEpisodeDischargeId && !filledIn;
  const editor = locationEpisodeDischarge.createdBy?.fullName;

  if (!filledIn && !edited) return null;

  return (
    <Alert style={{ marginBottom: 24 }} data-testid='pccDischargeBanner'>
      <Alert.Text>
        Filled in from PointClickCare on {dateAtTimeNoRelative(report.createdAt)}.
        {edited &&
          ` Edited${editor ? ` by ${editor}` : ''} on ${dateAtTimeNoRelative(locationEpisodeDischarge.createdAt)}.`}
      </Alert.Text>
      {filledIn && report.reviewReason && <Alert.Text>{REVIEW_REASON_NOTES[report.reviewReason]}</Alert.Text>}
    </Alert>
  );
}

export default function PccDischargeDetails({ report }: Pick<Props, 'report'>) {
  const { details } = report;

  const dischargedAt =
    details.dischargedAt &&
    (DATE_ONLY.test(details.dischargedAt)
      ? simpleDate(details.dischargedAt)
      : dateAtTimeNoRelative(details.dischargedAt));
  const status = [details.statusCode, details.statusDisplay].filter(Boolean).join(' · ');

  const rows = [
    { label: 'Discharge date', value: dischargedAt },
    {
      label: 'Discharge status',
      value: details.statusText ? `${status} (PointClickCare sent ${details.statusText})` : status,
    },
    { label: 'Destination type', value: details.destinationType },
    { label: 'Destination', value: details.destination },
  ];

  return (
    <Sent open data-testid='pccDischargeDetails'>
      <Summary>What PointClickCare sent</Summary>
      <List>
        {rows.map(({ label, value }) => (
          <Row key={label}>
            <Term>{label}</Term>
            <Value>{value || 'Not sent'}</Value>
          </Row>
        ))}
      </List>
    </Sent>
  );
}

const Sent = styled.details`
  margin-top: 24px;
  border: 1px solid ${colors.black10};
  border-radius: 3px;
  padding: 12px 16px;
`;

const Summary = styled.summary`
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black75};
`;

const List = styled.dl`
  margin: 8px 0 0;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
  line-height: 18px;
`;

const Term = styled.dt`
  color: ${colors.black75};
`;

const Value = styled.dd`
  margin: 0;
  color: ${colors.black100};
`;
